import React, { useState, useEffect } from 'react';
import {
    Modal, Box, Typography, Button, Select, MenuItem,
    TextField, Paper, Autocomplete
} from '@mui/material';
import skillsData from "../data/skills.json"
import qualitiesData from "../data/qualities.json"
import { getEffective } from "./applyHomebrew"

const FilterModal = ({ open, handleClose, filter, setFilter, category, data }) => {
    const skills = getEffective('skills', skillsData);
    const qualityDefs = getEffective('qualities', qualitiesData);
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [selectedSkill, setSelectedSkill] = useState('');
    const [minRange, setMinRange] = useState('');
    const [maxRange, setMaxRange] = useState('');
    const [minDamage, setMinDamage] = useState('');
    const [maxDamage, setMaxDamage] = useState('');
    const [minCrit, setMinCrit] = useState('');
    const [maxCrit, setMaxCrit] = useState('');
    const [minSoak, setMinSoak] = useState('');
    const [maxSoak, setMaxSoak] = useState('');
    const [minDefense, setMinDefense] = useState('');
    const [maxDefense, setMaxDefense] = useState('');
    const [minEncumbrance, setMinEncumbrance] = useState('');
    const [maxEncumbrance, setMaxEncumbrance] = useState('');
    const [minSilhouette, setMinSilhouette] = useState('');
    const [maxSilhouette, setMaxSilhouette] = useState('');
    const [minSpeed, setMinSpeed] = useState('');
    const [maxSpeed, setMaxSpeed] = useState('');
    const [minArmor, setMinArmor] = useState('');
    const [maxArmor, setMaxArmor] = useState('');
    const [minHP, setMinHP] = useState('');
    const [maxHP, setMaxHP] = useState('');
    const [minRarity, setMinRarity] = useState('');
    const [maxRarity, setMaxRarity] = useState('');
    const [source, setSource] = useState('');
    const [selectedSources, setSelectedSources] = useState([]);
    const [restricted, setRestricted] = useState("All")
    const [selectedQualities, setSelectedQualities] = useState([]);

    useEffect(() => {
        setMinPrice('');
        setMaxPrice('');
        setSelectedSkill('');
        setMinRange('');
        setMaxRange('');
        setMinDamage('');
        setMaxDamage('');
        setMinCrit('');
        setMaxCrit('');
        setMinSoak('');
        setMaxSoak('');
        setMinDefense('');
        setMaxDefense('');
        setMinEncumbrance('');
        setMaxEncumbrance('');
        setMinSilhouette('');
        setMaxSilhouette('');
        setMinSpeed('');
        setMaxSpeed('');
        setMinArmor('');
        setMaxArmor('');
        setMinHP('');
        setMaxHP('');
        setMinRarity('');
        setMaxRarity('');
        setSelectedQualities([]);
        setFilter('')
    }, [category]);

    let skillsAvailable = []
    if (data && data.length > 0) {
        const uniqueSkillKeys = new Set();

        data.forEach(item => {
            if (item.SkillKey) {
                uniqueSkillKeys.add(item.SkillKey);
            }
        });
        skillsAvailable = Array.from(uniqueSkillKeys);
    }

    const itemQualityKeys = (item) => {
        const list = Array.isArray(item.Qualities) ? item.Qualities : item.Qualities ? [item.Qualities] : []
        return list.map(q => q?.Key).filter(Boolean)
    }
    const qualityName = (key) => qualityDefs.find(q => q.Key === key)?.Name || key

    let qualitiesAvailable = []
    if (data && data.length > 0) {
        const uniqueQualityKeys = new Set(selectedQualities);
        data.forEach(item => itemQualityKeys(item).forEach(k => uniqueQualityKeys.add(k)));
        qualitiesAvailable = Array.from(uniqueQualityKeys).sort((a, b) => qualityName(a).localeCompare(qualityName(b)));
    }

    let sources = []
    if (data && data.length > 0) {
        const uniqueSources = new Set();

        data.forEach(item => {
            if (item.Source) {
                if (Array.isArray(item.Source)) {
                    item.Source.forEach(source => {
                        if (typeof source === 'object' && source !== null && source.hasOwnProperty('__text')) {
                            uniqueSources.add(source.__text);
                        } else if (typeof source === 'string') {
                            uniqueSources.add(source);
                        } else {
                            console.warn("Unexpected source type in array:", source);
                        }
                    });
                } else {
                    if (typeof item.Source === 'object' && item.Source !== null && item.Source.hasOwnProperty('__text')) {
                        uniqueSources.add(item.Source.__text);
                    } else if (typeof item.Source === 'string') {
                        uniqueSources.add(item.Source);
                    } else {
                        console.warn("Unexpected source type:", item.Source);
                    }
                }
            }
        });
        sources = Array.from(uniqueSources);
    }

    const rangeValues = ["Engaged", "Short", "Medium", "Long", "Extreme"];

    const applyFilters = () => {
        const filterFunction = (item) => {
            const rangeValues = ["Engaged", "Short", "Medium", "Long", "Extreme"];
            const getRangeIndex = (range) => rangeValues.indexOf(range);

            const checkRange = (value, min, max) => {
                const numValue = parseInt(value);
                const minValue = parseInt(min);
                const maxValue = parseInt(max);
                return (!minValue || (numValue >= minValue)) && (!maxValue || (numValue <= maxValue));
            };

            const checkCustomRange = (value, min, max) => {
                const rangeIndex = getRangeIndex(value);
                const minIndex = getRangeIndex(min);
                const maxIndex = getRangeIndex(max);
                return ((!min || (rangeIndex >= minIndex)) && (!max || (rangeIndex <= maxIndex)));
            };

            const checkSource = (itemSource) => {
                if (!selectedSources || selectedSources.length === 0) {
                    return true;
                }

                if (typeof itemSource === 'string') {
                    return selectedSources.includes(itemSource);
                } else if (Array.isArray(itemSource)) {
                    return itemSource.some(source => {
                        if (typeof source === 'object' && source !== null && source.hasOwnProperty('__text')) {
                            return selectedSources.includes(source.__text);
                        } else if (typeof source === 'string') {
                            return selectedSources.includes(source);
                        }
                        return false;
                    });
                } else if (typeof itemSource === 'object' && itemSource !== null && itemSource.hasOwnProperty('__text')) {
                    return selectedSources.includes(itemSource.__text);
                }
                return false;
            };

            const checkRestricted = (restriction) => {
                return (restricted === "All" || (restricted === "Unrestricted" && !restriction) || (restricted === "Restricted" && restriction))
            }

            return (
                checkRestricted(item.Restricted) &&
                checkRange(item.Price, minPrice, maxPrice) &&
                checkRange(item.Rarity, minRarity, maxRarity) &&
                (!selectedSkill || item.SkillKey === selectedSkill) &&
                checkSource(item.Source) &&
                (category !== "Weapons" || selectedQualities.every(k => itemQualityKeys(item).includes(k))) &&
                (category === "Gear" || checkRange(item.HP, minHP, maxHP)) &&
                (category !== "Weapons" || (category === "Weapons" &&
                    checkCustomRange(item.Range, minRange, maxRange) &&
                    checkRange(item.Damage, minDamage, maxDamage) &&
                    checkRange(item.Crit, minCrit, maxCrit))) &&
                (category !== "Armor" || (category === "Armor" &&
                    checkRange(item.Soak, minSoak, maxSoak) &&
                    checkRange(item.Defense, minDefense, maxDefense))) &&
                ((category !== "Vehicles" &&
                    checkRange(item.Encumbrance, minEncumbrance, maxEncumbrance)) ||
                    (category === "Vehicles" &&
                        checkRange(item.Silhouette, minSilhouette, maxSilhouette) &&
                        checkRange(item.Speed, minSpeed, maxSpeed) &&
                        checkRange(item.Armor, minArmor, maxArmor)))
            );
        };

        setFilter(() => filterFunction);
        handleClose();
    };

    return (
        <Modal open={open} onClose={handleClose}
            sx={{
                overflow: "auto"
            }}>
            <Box sx={{
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4
            }}>
                <Typography>Price:</Typography>
                <TextField label="Min" value={minPrice} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinPrice(e.target.value) : ""} />
                <TextField label="Max" value={maxPrice} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxPrice(e.target.value) : ""} />

                {category === "Weapons" && <>
                    <Typography>Skill</Typography>
                    <Select value={selectedSkill} onChange={(e) => setSelectedSkill(e.target.value)} sx={{ minWidth: "100px" }}>
                        <MenuItem value="">None</MenuItem>
                        {skills.filter(s => skillsAvailable.includes(s.Key) || s.Key === selectedSkill).map(skill => (
                            <MenuItem key={skill.Key} value={skill.Key}>
                                {skill.skill}
                            </MenuItem>
                        ))}
                    </Select>

                    <Typography>Range</Typography>
                    <Select value={minRange} onChange={(e) => setMinRange(e.target.value)} sx={{ minWidth: "100px" }}>
                        <MenuItem value="">None</MenuItem>
                        {rangeValues.map(range => (
                            <MenuItem key={range} value={range}>{range}</MenuItem>
                        ))}
                    </Select>
                    <Select value={maxRange} onChange={(e) => setMaxRange(e.target.value)} sx={{ minWidth: "100px" }}>
                        <MenuItem value="">None</MenuItem>
                        {rangeValues.map(range => (
                            <MenuItem key={range} value={range}>{range}</MenuItem>
                        ))}
                    </Select>

                    <Typography>Damage:</Typography>
                    <TextField label="Min" value={minDamage} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinDamage(e.target.value) : ""} />
                    <TextField label="Max" value={maxDamage} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxDamage(e.target.value) : ""} />

                    <Typography>Crit:</Typography>
                    <TextField label="Min" value={minCrit} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinCrit(e.target.value) : ""} />
                    <TextField label="Max" value={maxCrit} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxCrit(e.target.value) : ""} />
                </>}

                {category === "Armor" && <>
                    <Typography>Soak</Typography>
                    <TextField label="Min" value={minSoak} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinSoak(e.target.value) : ""} />
                    <TextField label="Max" value={maxSoak} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxSoak(e.target.value) : ""} />
                    <Typography>Defense:</Typography>
                    <TextField label="Min" value={minDefense} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinDefense(e.target.value) : ""} />
                    <TextField label="Max" value={maxDefense} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxDefense(e.target.value) : ""} />
                </>}

                {category === "Weapons" && <>
                    <Typography>Qualities</Typography>
                    <Autocomplete
                        multiple
                        options={qualitiesAvailable}
                        getOptionLabel={(option) => qualityName(option)}
                        value={selectedQualities}
                        onChange={(event, newValue) => setSelectedQualities(newValue)}
                        filterSelectedOptions
                        renderInput={(params) => (
                            <TextField {...params} label="Qualities" placeholder="Has all of" />
                        )}
                        sx={{ width: 500 }}
                    />
                </>}

                {category !== "Vehicles" && <>
                    <Typography>Encumbrance:</Typography>
                    <TextField label="Min" value={minEncumbrance} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinEncumbrance(e.target.value) : ""} />
                    <TextField label="Max" value={maxEncumbrance} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxEncumbrance(e.target.value) : ""} />
                </>}

                {category === "Vehicles" && <>
                    <Typography>Silhouette</Typography>
                    <TextField label="Min" value={minSilhouette} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinSilhouette(e.target.value) : ""} />
                    <TextField label="Max" value={maxSilhouette} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxSilhouette(e.target.value) : ""} />
                    <Typography>Speed:</Typography>
                    <TextField label="Min" value={minSpeed} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinSpeed(e.target.value) : ""} />
                    <TextField label="Max" value={maxSpeed} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxSpeed(e.target.value) : ""} />
                    <Typography>Armor:</Typography>
                    <TextField label="Min" value={minArmor} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinArmor(e.target.value) : ""} />
                    <TextField label="Max" value={maxArmor} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxArmor(e.target.value) : ""} />
                </>}

                {category !== "Gear" && <>
                    <Typography>Hard Points:</Typography>
                    <TextField label="Min" value={minHP} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinHP(e.target.value) : ""} />
                    <TextField label="Max" value={maxHP} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxHP(e.target.value) : ""} />
                </>}
                <Typography>Rarity:</Typography>
                <TextField label="Min" value={minRarity} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMinRarity(e.target.value) : ""} />
                <TextField label="Max" value={maxRarity} onChange={(e) => (!isNaN(parseInt(e.target.value)) || e.target.value === "") ? setMaxRarity(e.target.value) : ""} />
                <br />
                <Typography>Restricted</Typography>
                <Select value={restricted} onChange={(e) => setRestricted(e.target.value)} sx={{ minWidth: "100px" }}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Unrestricted">Exclude Restricted</MenuItem>
                    <MenuItem value="Restricted">Restricted Only</MenuItem>
                </Select>
                <br />
                <Typography>Source</Typography>
                <Autocomplete
                    multiple
                    id="sourceMultiSelect"
                    options={sources.sort(([a], [b]) => a.localeCompare(b))}
                    getOptionLabel={(option) => option}
                    value={selectedSources}
                    onChange={(event, newValue) => {
                        setSelectedSources(newValue);
                    }}
                    filterSelectedOptions
                    renderInput={(params) => (
                        <TextField {...params} label="Sources" placeholder="Sources" />
                    )}
                    sx={{ width: 500 }}
                />
                <br />
                <Button onClick={applyFilters}>Apply Filters</Button>
            </Box>
        </Modal>
    );
};

export default FilterModal;
